import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { eventsAPI } from "../services/schedulerAPI";
import { showToast } from "../components/Toast";
import EventSuccessModal from "../components/EventSuccessModal";
import RegistrationFormBuilder from "../components/RegistrationFormBuilder";

const CreateEvent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publishedEvent, setPublishedEvent] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [showAttendeeWarning, setShowAttendeeWarning] = useState(false);
  const [registrationFields, setRegistrationFields] = useState([
    { id: "full_name", label: "Full Name", type: "text", required: true, icon: "👤" },
    { id: "email", label: "Email", type: "email", required: true, icon: "📧" },
  ]);
  const autoSaveTimerRef = useRef(null);
  const initialDataRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    title: "",
    description: "",
    event_type: "virtual",
    category: "business",
    tags: [],

    // Date & Time
    start_datetime: "",
    end_datetime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    is_recurring: false,
    recurrence_pattern: "",

    // Location
    venue_name: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",

    // Visual Design
    cover_image_url: "",
    brand_colors: {
      primary: "#4F46E5",
      accent: "#06B6D4",
    },
    font_family: "Inter",
    template: "modern",

    // Capacity & Registration
    max_attendees: null,
    enable_waitlist: false,
    registration_deadline: "",
    require_approval: false,
    allow_guest_registration: true,

    // Visibility
    visibility: "public",
    show_attendee_count: true,
    show_attendee_list: false,
    status: "draft",
  });

  // Load event data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadEvent();
    }
  }, [id, isEditMode]);

  const loadEvent = async () => {
    setLoading(true);
    try {
      const event = await eventsAPI.get(id);
      const loadedData = {
        ...event,
        tags: event.tags || [],
        brand_colors: event.brand_colors || { primary: "#4F46E5", accent: "#06B6D4" },
      };
      setFormData(loadedData);
      setRegistrationFields(event.registration_fields || registrationFields);
      setAttendeeCount(event.attendee_count || 0);
      initialDataRef.current = loadedData;
    } catch (error) {
      console.error("Failed to load event:", error);
      showToast.error("Failed to load event");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  // Auto-save draft every 30 seconds
  const autoSave = useCallback(async () => {
    if (!hasUnsavedChanges || saving || !isEditMode) return;

    try {
      const eventData = {
        ...formData,
        registration_fields: registrationFields,
        status: formData.status || "draft"
      };

      await eventsAPI.update(id, eventData);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      initialDataRef.current = formData;
    } catch (error) {
      console.error("Auto-save failed:", error);
      // Silent failure for auto-save
    }
  }, [formData, registrationFields, hasUnsavedChanges, saving, isEditMode, id]);

  // Set up auto-save timer
  useEffect(() => {
    if (isEditMode) {
      autoSaveTimerRef.current = setInterval(autoSave, 30000); // 30 seconds
      return () => {
        if (autoSaveTimerRef.current) {
          clearInterval(autoSaveTimerRef.current);
        }
      };
    }
  }, [isEditMode, autoSave]);

  // Track unsaved changes
  useEffect(() => {
    if (!initialDataRef.current) {
      initialDataRef.current = formData;
      return;
    }

    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialDataRef.current) ||
                       JSON.stringify(registrationFields) !== JSON.stringify(initialDataRef.current.registration_fields || []);
    setHasUnsavedChanges(hasChanges);
  }, [formData, registrationFields]);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Validation function
  const validateForm = () => {
    const errors = {};

    // Basic Information
    if (!formData.title?.trim()) errors.title = "Title is required";
    if (!formData.description?.trim()) errors.description = "Description is required";

    // Date & Time
    if (!formData.start_datetime) errors.start_datetime = "Start date is required";
    if (!formData.end_datetime) errors.end_datetime = "End date is required";

    if (formData.start_datetime && formData.end_datetime) {
      if (new Date(formData.end_datetime) <= new Date(formData.start_datetime)) {
        errors.end_datetime = "End date must be after start date";
      }
    }

    // Location (for in-person/hybrid)
    if (formData.event_type === "in-person" || formData.event_type === "hybrid") {
      if (!formData.address_line1?.trim()) errors.address_line1 = "Address is required";
      if (!formData.city?.trim()) errors.city = "City is required";
      if (!formData.state?.trim()) errors.state = "State is required";
      if (!formData.postal_code?.trim()) errors.postal_code = "Postal code is required";
      if (!formData.country?.trim()) errors.country = "Country is required";
    }

    // Visual Design
    if (!formData.cover_image_url) errors.cover_image_url = "Cover image is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);

    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showToast.error("Image size must be less than 5MB");
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange("cover_image_url", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const eventData = {
        ...formData,
        status: "draft",
      };

      if (isEditMode) {
        await eventsAPI.update(id, eventData);
        showToast.success("Draft saved successfully!");
      } else {
        const created = await eventsAPI.create(eventData);
        showToast.success("Draft saved successfully!");
        navigate(`/events/${created.id}/edit`);
      }
    } catch (error) {
      console.error("Failed to save draft:", error);
      showToast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    // Validate form
    if (!validateForm()) {
      showToast.error("Please fix the errors before publishing");
      return;
    }

    // Check if event has attendees (editing mode)
    if (isEditMode && id && attendeeCount > 0) {
      setShowAttendeeWarning(true);
      return; // Wait for user confirmation
    }

    await handlePublishConfirmed();
  };

  const handlePublishConfirmed = async () => {
    setSaving(true);
    try {
      const eventData = {
        ...formData,
        registration_fields: registrationFields,
        status: "published",
      };

      let event;
      if (isEditMode) {
        event = await eventsAPI.update(id, eventData);
        await eventsAPI.publish(id);
        showToast.success("Event updated and published successfully!");
      } else {
        event = await eventsAPI.create(eventData);
        await eventsAPI.publish(event.id);
        showToast.success("Event published successfully!");
      }

      setPublishedEvent(event);
      setShowSuccessModal(true);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Failed to publish event:", error);
      showToast.error("Failed to publish event");
    } finally {
      setSaving(false);
    }
  };

  const sections = [
    {
      id: "basic",
      title: "Basic Information",
      icon: "📝",
      description: "Event details and description",
    },
    {
      id: "datetime",
      title: "Date & Time",
      icon: "📅",
      description: "When your event takes place",
    },
    {
      id: "location",
      title: "Location",
      icon: "📍",
      description: "Where your event takes place",
    },
    {
      id: "design",
      title: "Visual Design",
      icon: "🎨",
      description: "Customize your event page",
    },
    {
      id: "capacity",
      title: "Capacity & Registration",
      icon: "👥",
      description: "Attendee limits and registration settings",
    },
    {
      id: "visibility",
      title: "Visibility",
      icon: "👁️",
      description: "Who can see your event",
    },
    {
      id: "registration",
      title: "Registration Form",
      icon: "📋",
      description: "Customize registration fields",
    },
  ];

  // Attendee Warning Modal Component
  const AttendeeWarningModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
      >
        <div className="text-center mb-6">
          <div className="text-5xl mb-3">⚠️</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Update Existing Event?
          </h3>
          <p className="text-gray-600 text-sm">
            This event has <strong>{attendeeCount} registered attendee{attendeeCount !== 1 ? 's' : ''}</strong>.
            Updating it will notify all attendees of the changes.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={async () => {
              setShowAttendeeWarning(false);
              await handlePublishConfirmed();
            }}
            className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            Yes, Update & Notify Attendees
          </button>
          <button
            onClick={() => setShowAttendeeWarning(false)}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
          >
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isEditMode ? "Edit Event Page" : "Create Event Page"}
          </h1>
          <p className="text-gray-600">
            Create a beautiful event landing page with custom branding
          </p>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Section Navigation */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl shadow-sm p-4 sticky top-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => setCurrentSection(index)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      currentSection === index
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <span className="mr-2">{section.icon}</span>
                    <span className="text-sm">{section.title}</span>
                  </button>
                ))}
              </nav>

              {/* Unsaved Changes Indicator */}
              {hasUnsavedChanges && (
                <div className="mt-4 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center gap-2 text-yellow-800 text-sm">
                    <span>⚠️</span>
                    <span className="font-medium">Unsaved changes</span>
                  </div>
                  {lastSaved && (
                    <p className="text-xs text-yellow-600 mt-1">
                      Last saved: {new Date(lastSaved).toLocaleTimeString()}
                    </p>
                  )}
                </div>
              )}

              {!hasUnsavedChanges && lastSaved && isEditMode && (
                <div className="mt-4 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800 text-sm">
                    <span>✓</span>
                    <span className="font-medium">All changes saved</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    {new Date(lastSaved).toLocaleTimeString()}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
                <button
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save Draft"}
                </button>
                <button
                  onClick={handlePublish}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
                >
                  {saving ? "Publishing..." : "Publish Event"}
                </button>
                <button
                  onClick={() => navigate("/events")}
                  className="w-full px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <motion.div
                key={currentSection}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                {/* Section Header */}
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">
                    {sections[currentSection].icon} {sections[currentSection].title}
                  </h2>
                  <p className="text-gray-600 text-sm">{sections[currentSection].description}</p>
                </div>

                {/* Basic Information Section */}
                {currentSection === 0 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Title <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleChange("title", e.target.value)}
                        placeholder="e.g., Product Launch 2024"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleChange("description", e.target.value)}
                        placeholder="Describe your event..."
                        rows={6}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.event_type}
                        onChange={(e) => handleChange("event_type", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="in-person">In-person</option>
                        <option value="virtual">Virtual</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleChange("category", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="business">Business</option>
                        <option value="tech">Tech</option>
                        <option value="social">Social</option>
                        <option value="education">Education</option>
                        <option value="networking">Networking</option>
                        <option value="conference">Conference</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
                      <input
                        type="text"
                        placeholder="Enter tags separated by commas"
                        value={formData.tags.join(", ")}
                        onChange={(e) =>
                          handleChange(
                            "tags",
                            e.target.value.split(",").map((t) => t.trim()).filter(Boolean)
                          )
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        e.g., networking, startup, product-launch
                      </p>
                    </div>
                  </div>
                )}

                {/* Date & Time Section */}
                {currentSection === 1 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date & Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.start_datetime}
                        onChange={(e) => handleChange("start_datetime", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date & Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.end_datetime}
                        onChange={(e) => handleChange("end_datetime", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Timezone <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => handleChange("timezone", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="America/New_York">Eastern Time (ET)</option>
                        <option value="America/Chicago">Central Time (CT)</option>
                        <option value="America/Denver">Mountain Time (MT)</option>
                        <option value="America/Los_Angeles">Pacific Time (PT)</option>
                        <option value="Europe/London">London (GMT)</option>
                        <option value="Europe/Paris">Paris (CET)</option>
                        <option value="Asia/Tokyo">Tokyo (JST)</option>
                        <option value="Asia/Dubai">Dubai (GST)</option>
                        <option value="Australia/Sydney">Sydney (AEDT)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-detected: {Intl.DateTimeFormat().resolvedOptions().timeZone}
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.is_recurring}
                          onChange={(e) => handleChange("is_recurring", e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Recurring Event
                        </span>
                      </label>
                    </div>

                    {formData.is_recurring && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recurrence Pattern
                        </label>
                        <select
                          value={formData.recurrence_pattern}
                          onChange={(e) => handleChange("recurrence_pattern", e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select pattern...</option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="biweekly">Bi-weekly</option>
                          <option value="monthly">Monthly</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Location Section */}
                {currentSection === 2 && (
                  <div className="space-y-6">
                    {(formData.event_type === "in-person" || formData.event_type === "hybrid") && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Venue Name
                          </label>
                          <input
                            type="text"
                            value={formData.venue_name}
                            onChange={(e) => handleChange("venue_name", e.target.value)}
                            placeholder="e.g., Convention Center"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Address Line 1 <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.address_line1}
                            onChange={(e) => handleChange("address_line1", e.target.value)}
                            placeholder="Street address"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Address Line 2
                          </label>
                          <input
                            type="text"
                            value={formData.address_line2}
                            onChange={(e) => handleChange("address_line2", e.target.value)}
                            placeholder="Apartment, suite, etc. (optional)"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              City <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.city}
                              onChange={(e) => handleChange("city", e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              State/Province <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.state}
                              onChange={(e) => handleChange("state", e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Postal Code <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.postal_code}
                              onChange={(e) => handleChange("postal_code", e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Country <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.country}
                              onChange={(e) => handleChange("country", e.target.value)}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {formData.event_type === "virtual" && (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">💻</div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Virtual Event
                        </h3>
                        <p className="text-gray-600">
                          No physical location required. Meeting details will be sent to attendees
                          after registration.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Visual Design Section */}
                {currentSection === 3 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cover Image <span className="text-red-500">*</span>
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                        {formData.cover_image_url ? (
                          <div className="relative">
                            <img
                              src={formData.cover_image_url}
                              alt="Cover"
                              className="max-h-64 mx-auto rounded-lg"
                            />
                            <button
                              onClick={() => handleChange("cover_image_url", "")}
                              className="absolute top-2 right-2 px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <div>
                            <div className="text-4xl mb-2">📸</div>
                            <label className="cursor-pointer">
                              <span className="text-blue-600 hover:text-blue-700 font-medium">
                                Upload an image
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                              />
                            </label>
                            <p className="text-xs text-gray-500 mt-2">
                              PNG, JPG up to 5MB. Recommended: 1200x630px
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Brand Color
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.brand_colors.primary}
                            onChange={(e) =>
                              handleNestedChange("brand_colors", "primary", e.target.value)
                            }
                            className="h-10 w-20 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.brand_colors.primary}
                            onChange={(e) =>
                              handleNestedChange("brand_colors", "primary", e.target.value)
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Accent Color
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="color"
                            value={formData.brand_colors.accent}
                            onChange={(e) =>
                              handleNestedChange("brand_colors", "accent", e.target.value)
                            }
                            className="h-10 w-20 rounded cursor-pointer"
                          />
                          <input
                            type="text"
                            value={formData.brand_colors.accent}
                            onChange={(e) =>
                              handleNestedChange("brand_colors", "accent", e.target.value)
                            }
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Font Family
                      </label>
                      <select
                        value={formData.font_family}
                        onChange={(e) => handleChange("font_family", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="Inter">Inter</option>
                        <option value="Roboto">Roboto</option>
                        <option value="Open Sans">Open Sans</option>
                        <option value="Poppins">Poppins</option>
                        <option value="Lato">Lato</option>
                        <option value="Montserrat">Montserrat</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Template Selection
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        {["modern", "minimal", "bold", "elegant", "playful", "corporate"].map(
                          (template) => (
                            <button
                              key={template}
                              onClick={() => handleChange("template", template)}
                              className={`p-4 border-2 rounded-lg transition-all ${
                                formData.template === template
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                              }`}
                            >
                              <div className="text-2xl mb-2">
                                {template === "modern" && "✨"}
                                {template === "minimal" && "⚪"}
                                {template === "bold" && "💥"}
                                {template === "elegant" && "👔"}
                                {template === "playful" && "🎨"}
                                {template === "corporate" && "💼"}
                              </div>
                              <div className="text-sm font-medium text-gray-700 capitalize">
                                {template}
                              </div>
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Capacity & Registration Section */}
                {currentSection === 4 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum Attendees
                      </label>
                      <input
                        type="number"
                        value={formData.max_attendees || ""}
                        onChange={(e) =>
                          handleChange("max_attendees", e.target.value ? parseInt(e.target.value) : null)
                        }
                        placeholder="Leave empty for unlimited"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.enable_waitlist}
                          onChange={(e) => handleChange("enable_waitlist", e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Enable Waitlist
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Allow people to join a waitlist when event is full
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Registration Deadline
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.registration_deadline}
                        onChange={(e) => handleChange("registration_deadline", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.require_approval}
                          onChange={(e) => handleChange("require_approval", e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Require Approval
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Manually approve each registration
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.allow_guest_registration}
                          onChange={(e) =>
                            handleChange("allow_guest_registration", e.target.checked)
                          }
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Allow Guest Registration
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Let people register without creating an account
                      </p>
                    </div>
                  </div>
                )}

                {/* Visibility Section */}
                {currentSection === 5 && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Event Visibility
                      </label>
                      <select
                        value={formData.visibility}
                        onChange={(e) => handleChange("visibility", e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="public">Public - Anyone can find and view</option>
                        <option value="unlisted">Unlisted - Only people with link can view</option>
                        <option value="private">Private - Invite only</option>
                      </select>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.show_attendee_count}
                          onChange={(e) => handleChange("show_attendee_count", e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Show Attendee Count
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Display number of registered attendees
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.show_attendee_list}
                          onChange={(e) => handleChange("show_attendee_list", e.target.checked)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-medium text-gray-700">
                          Show Attendee List
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        Display list of registered attendees publicly
                      </p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                      <h4 className="font-medium text-blue-900 mb-2">Preview</h4>
                      <p className="text-sm text-blue-800">
                        Your event page will be available at: <br />
                        <code className="bg-blue-100 px-2 py-1 rounded mt-1 inline-block">
                          {window.location.origin}/e/
                          {formData.title.toLowerCase().replace(/\s+/g, "-") || "your-event"}
                        </code>
                      </p>
                    </div>
                  </div>
                )}

                {/* Registration Form Section */}
                {currentSection === 6 && (
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <h4 className="font-medium text-blue-900 mb-2">Build Your Registration Form</h4>
                      <p className="text-sm text-blue-800">
                        Drag and drop fields to customize what information you collect from attendees.
                        Full Name and Email are required by default.
                      </p>
                    </div>

                    <RegistrationFormBuilder
                      fields={registrationFields}
                      onChange={(fields) => {
                        setRegistrationFields(fields);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
                    disabled={currentSection === 0}
                    className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    ← Previous
                  </button>

                  {currentSection < sections.length - 1 ? (
                    <button
                      onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Next →
                    </button>
                  ) : (
                    <button
                      onClick={handlePublish}
                      disabled={saving}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {saving ? "Publishing..." : "Publish Event 🚀"}
                    </button>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Success Modal */}
        <AnimatePresence>
          {showSuccessModal && publishedEvent && (
            <EventSuccessModal
              event={publishedEvent}
              onClose={() => {
                setShowSuccessModal(false);
                navigate("/events");
              }}
            />
          )}
        </AnimatePresence>

        {/* Attendee Warning Modal */}
        <AnimatePresence>
          {showAttendeeWarning && <AttendeeWarningModal />}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CreateEvent;
